/* eslint-disable no-unused-vars */

import { ReactNode } from 'react';
import { createEntityAdapter, EntityState } from '@reduxjs/toolkit';
import ConversationsClient from '@twilio/conversations';
import { Conversation } from '@twilio/conversations/lib/conversation';
import { User } from '@twilio/conversations/lib/user';
import { Participant } from '@twilio/conversations/lib/participant';
import { Message } from '@twilio/conversations/lib/message';
import { Paginator } from '@twilio/conversations/lib/interfaces/paginator';
import { Color as AlertVariant } from '@material-ui/lab/Alert';

export type {
  ConversationsClient,
  Conversation,
  User,
  Participant,
  Message,
  Paginator,
};

export enum ConnectionState {
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  DISCONNECTING = 'disconnecting',
  DISCONNECTED = 'disconnected',
  DENIED = 'denied',
} // Twilio client uses lower case

export enum ConversationType {
  PRIVATE = 'PRIVATE',
  GROUP = 'GROUP',
  ANNOUNCEMENT = 'ANNOUNCEMENT',
  PUBLIC = 'PUBLIC',
  UNKNOWN = 'UNKNOWN',
}

export enum MessageDirection {
  OUTGOING = 'OUTGOING',
  INCOMING = 'INCOMING',
}

export enum MessageType {
  Text = 'Text',
  File = 'File',
  ParticipantJoined = 'ParticipantJoined',
  ParticipantLeft = 'ParticipantLeft',
  AudioCall = 'AudioCall',
  VideoCall = 'VideoCall',
  ContactCard = 'ContactCard',
}

export enum VideoCallMessageType {
  VideoCallAccept = 'VideoCallAccept',
  VideoCallInvite = 'VideoCallInvite',
  VideoCallHangup = 'VideoCallHangup',
  VideoCallJoinRequest = 'VideoCallJoinRequest',
  VideoCallJoinApprove = 'VideoCallJoinApprove',
  VideoCallNotSupport = 'VideoCallNotSupport',
  VideoCallSummary = 'VideoCallSummary',
}

export interface VideoCallResponseParams {
  targetId: string;
  name: string;
  roomToken: string;
  isTimeout?: boolean;
}

export interface VideoMessage {
  type: VideoCallMessageType;
  content: VideoCallResponseParams;
  dateSent: Date;
  author: string;
}

export interface ConversationAttributes {
  type: ConversationType;
  video?: VideoMessage[];
}

export interface ConversationMetadata {
  id: string;
  conversationType: ConversationType;
  hasLoadedMessages: boolean;
  hasPrevPage?: boolean;
  prevPage?: () => Promise<Paginator<Message>>;
  hasNextPage?: boolean;
  nextPage?: () => Promise<Paginator<Message>>;
  lastReadMessageIndex: number;
  lastFetchedMessageIndex: number;
  lastMessage: TwilioMessage | null; // null if there isn't any messages in the conversation yet
  dateCreated: Date;
  isLoading: boolean;
  uniqueName: string | null;
}

export interface PrivateConversationMetadata
  extends ConversationMetadata {
  otherUserId: string;
}

export interface GroupMemberListMeta {
  otherUserIds: string[];
  fetchedAllParticipants: boolean;
}

export type PublicConversationMetadata = ConversationMetadata & GroupMemberListMeta;

export interface GroupConversationMetadata
  extends ConversationMetadata, GroupMemberListMeta {
  conversationName: string;
}

export enum GroupChatType {
  Booth = 'Booth',
  Webinar = 'Webinar',
}

export enum UserReachabilityStatus {
  OFFLINE = 'offline',
  IDLE = 'idle',
  ONLINE = 'online',
}

export interface TwilioUser {
  id: string; // Identity
  name?: string; // Display Name
  tag?: string; // User Tag
  description?: string; // User Description
  exhibitorBoothId?: string;
  email?: string;
  role?: string;
  isSelf: boolean;
  reachabilityStatus: UserReachabilityStatus;
}

export interface TwilioMessage {
  id: string;
  author: string;
  body: string;
  index: number;
  dateCreated: Date;
  attributes: TwilioMessageAttributes;
}

export interface TwilioMessageAttributes {
  messageType?: MessageType;
  file?: FileMessage;
  actionUuid?: string;
  contact?: ContactMessage;
  videoCallSummary?: VideoCallSummaryMessage;
}

export interface TwilioUserAttributes {
  userTag: string | null;
  userDescription: string | null;
  boothId: string | null;
  name: string;
  email: string;
  uuid: string;
  role: string;
  domain: string;
  isIdle?: boolean;
}

export interface ChatAlert {
  message: string | ReactNode;
  translationKey?: string;
  severity: AlertVariant;
}

export type ConversationTypeHandlerBuilder<T> = {
  [type in ConversationType]: T;
}

export interface FileMessage {
  name: string;
  size: number;
  type: string;
  url: string;
}

export interface ContactMessage {
  name: string;
  firstName: string,
  lastName: string,
  email: string;
  description: string;
  contactId: string;
}

export interface VideoCallSummaryMessage {
  duration: number;
  roomToken: string;
}

export interface MessageAuthorGroup {
  author: string;
  direction: MessageDirection;
  renderSender: boolean;
  messages: TwilioMessage[];
  startMessageIndex: number;
}

export type MessageDisplay = {
  dateString: string;
  messageGroups: MessageAuthorGroup[];
}[];

export type DateMessageMap = Record<string, TwilioMessage[]>;

export type SetNotificationPayload = {
  settingTarget: ConversationType;
  switchValue: boolean;
}

export type SetCollapsePayload = {
  type: ConversationType;
  value: boolean;
}

export const userAdapter = createEntityAdapter<TwilioUser>();

export const metadataAdapter = createEntityAdapter<ConversationMetadata>();

export const messageAdapter = createEntityAdapter<TwilioMessage>({
  selectId: (message) => message.id,
  sortComparer: (prevMessage, nextMessage) => prevMessage.index - nextMessage.index,
});

export const initialState = {
  client: null as ConversationsClient,
  users: userAdapter.getInitialState(),
  connectionState: 'connecting' as ConnectionState,
  chatInterfaceActive: false,
  metadata: metadataAdapter.getInitialState(),
  selectedChat: null as string,
  messages: {} as Record<string, EntityState<TwilioMessage>>,
  alert: null as ChatAlert,
  imageViewerInitial: null as string,
  allConversationsLoaded: false,
};

export type TwilioSliceType = typeof initialState;
